import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, FileText, Truck, Search, Phone } from "lucide-react";

/**
 * Branded 404 for Fortune Sourcings.
 * Direct, useful, on-brand. No "set sail" metaphor. Lists the most likely
 * destinations so no visitor ever hits a real dead end.
 */

const QUICK_LINKS = [
  { to: "/services", label: "Our Services", desc: "Sourcing, QC, freight, hotel FF&E" },
  { to: "/corridors", label: "Import Corridors", desc: "China → India · Nepal · Bhutan" },
  { to: "/fees", label: "Fees & Payment", desc: "Published fee model, 30/50/20 schedule" },
  { to: "/china-business-trips", label: "China Business Trips", desc: "Book a hosted factory-visit trip" },
  { to: "/insights", label: "Insights", desc: "Sourcing playbooks and briefings" },
  { to: "/tools", label: "Buyer Tools", desc: "Cost calculators and compliance lookups" },
];

export default function NotFound() {
  const { pathname } = useLocation();

  return (
    <div data-testid="not-found-page" className="bg-[#12283F] text-white min-h-[80vh]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-20 lg:py-24">

        {/* HEADER */}
        <div className="grid lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-[#B8965A]">404 · Page Not Found</span>
            </div>
            <h1 className="font-serif text-white text-[44px] sm:text-[56px] lg:text-[72px] leading-[0.98] tracking-tight mb-6">
              We couldn&apos;t find <span className="text-[#B8965A]">that page.</span>
            </h1>
            <p className="text-white/75 text-[16px] lg:text-lg max-w-2xl mb-8 leading-relaxed">
              The URL <code className="bg-white/10 px-2 py-0.5 text-[13px] rounded">{pathname}</code> isn&apos;t part of the current site. That page may have moved, been renamed, or never existed. Below is where most visitors are heading — or reach us directly.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="btn-gold" data-testid="404-home-cta">
                <HomeIcon size={16}/> Homepage <ArrowRight size={16}/>
              </Link>
              <Link to="/submit-rfq" className="btn-outline text-white border-white/30 hover:bg-white hover:text-[#1C3E5F]" data-testid="404-rfq-cta">
                <FileText size={16}/> Submit an RFQ
              </Link>
              <Link to="/contact" className="btn-outline text-white border-white/30 hover:bg-white hover:text-[#1C3E5F]" data-testid="404-contact-cta">
                <Phone size={16}/> Contact us
              </Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="number-display text-[220px] leading-none opacity-95 text-right">404</div>
            <div className="eyebrow text-[#B8965A] text-right mt-2">Fortune Sourcings · Est. 1991</div>
          </div>
        </div>

        {/* QUICK LINKS GRID */}
        <div className="border-t border-white/10 pt-12">
          <div className="eyebrow text-[#B8965A] mb-8">Most likely destinations</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {QUICK_LINKS.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="group block bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#B8965A] p-5 transition-colors"
                data-testid={`404-link-${q.to.replace("/", "")}`}
              >
                <div className="font-serif text-white text-xl leading-tight group-hover:text-[#B8965A] transition-colors mb-1">{q.label}</div>
                <div className="text-white/60 text-[13px]">{q.desc}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA STRIP */}
        <div className="mt-14 grid md:grid-cols-2 gap-4">
          <a
            href="https://wa.me/8613169440367"
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-4 bg-[#25D366]/10 border border-[#25D366]/30 hover:border-[#25D366] p-5 transition-colors"
            data-testid="404-whatsapp-cta"
          >
            <div className="w-12 h-12 bg-[#25D366]/20 flex items-center justify-center shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/></svg>
            </div>
            <div>
              <div className="text-white font-serif text-lg">WhatsApp us</div>
              <div className="text-white/60 text-[13px]">Response typically within 20 minutes during Asia hours</div>
            </div>
          </a>
          <a
            href="tel:+919692969221"
            className="group flex items-center gap-4 bg-white/[0.04] border border-white/10 hover:border-[#B8965A] p-5 transition-colors"
            data-testid="404-phone-cta"
          >
            <div className="w-12 h-12 bg-[#B8965A]/15 flex items-center justify-center shrink-0">
              <Phone size={22} className="text-[#B8965A]" />
            </div>
            <div>
              <div className="text-white font-serif text-lg">Call the India desk</div>
              <div className="text-white/60 text-[13px]">+91 96929 69221 · Siliguri · IST business hours</div>
            </div>
          </a>
        </div>

        {/* FOOTER MICRO-LINKS */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-white/50">
          <Link to="/about" className="hover:text-[#B8965A]">About</Link>
          <Link to="/faq" className="hover:text-[#B8965A]">FAQ</Link>
          <Link to="/clusters" className="hover:text-[#B8965A]">Manufacturing Clusters</Link>
          <Link to="/import" className="hover:text-[#B8965A]">Country Compliance</Link>
          <Link to="/insights" className="hover:text-[#B8965A]">Insights</Link>
          <Link to="/legal/privacy" className="hover:text-[#B8965A]">Privacy</Link>
        </div>
      </div>
    </div>
  );
}
